import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ================= FETCH ALL ORDERS (ADMIN) ================= */
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async ({ page = 1, limit = 10, status = "" } = {}, thunkAPI) => {
    try {
      const res = await api.get(
        `/api/orders?page=${page}&limit=${limit}&status=${status}`
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch orders");
    }
  }
);

/* ================= FETCH SINGLE ORDER (ADMIN) ================= */
export const fetchOrderDetails = createAsyncThunk(
  "orders/fetchOrderDetails",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/orders/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch order details"
      );
    }
  }
);

/* ================= UPDATE ORDER STATUS (ADMIN) ================= */
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ orderId, orderStatus }, thunkAPI) => {
    try {
      const res = await api.put(`/api/orders/${orderId}/status`, { orderStatus });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update order status"
      );
    }
  }
);

/* ================= FETCH MY ORDERS (USER) ================= */
export const fetchMyOrders = createAsyncThunk(
  "orders/fetchMyOrders",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/api/orders/my-orders`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch user orders"
      );
    }
  }
);

/* ================= UPDATE TRACKING URL ================= */
export const updateTrackingUrl = createAsyncThunk(
  "orders/updateTrackingUrl",
  async ({ orderId, trackingUrl }, thunkAPI) => {
    try {
      const res = await api.put(`/api/orders/${orderId}/tracking`, { trackingUrl });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update tracking URL"
      );
    }
  }
);

/* ================= SLICE ================= */
const orderSlice = createSlice({
  name: "orders",
  initialState: {
    orders: [],
    myOrders: [],
    orderDetails: null,
    loading: false,
    updating: false,
    error: null,
    pagination: {},
  },
  reducers: {
    clearOrderError:   (state) => { state.error = null; },
    clearOrderDetails: (state) => { state.orderDetails = null; },
  },
  extraReducers: (builder) => {
    builder

      /* ── FETCH ORDERS ── */
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading    = false;
        state.orders     = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── FETCH ORDER DETAILS ── */
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.loading      = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── UPDATE ORDER STATUS ── */
      .addCase(updateOrderStatus.pending, (state) => {
        state.updating = true;
        state.error    = null;
      })
      /*
        ✅ FIX: after a status update the order no longer belongs to the
        current tab, so remove it from the list immediately so the UI
        updates without a full reload. The re-fetch in OrderList keeps
        the count correct.
      */
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.updating = false;
        const updatedOrder = action.payload;
        if (updatedOrder?._id) {
          state.orders = state.orders.filter((o) => o._id !== updatedOrder._id);
          if (state.pagination?.total) {
            state.pagination.total -= 1;
          }
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updating = false;
        state.error    = action.payload;
      })

      /* ── FETCH MY ORDERS ── */
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading  = false;
        state.myOrders = action.payload;
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── UPDATE TRACKING URL ── */
      .addCase(updateTrackingUrl.pending, (state) => {
        state.updating = true;
        state.error    = null;
      })
      .addCase(updateTrackingUrl.fulfilled, (state, action) => {
        state.updating = false;
        const updatedOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === updatedOrder._id);
        if (index !== -1) state.orders[index] = updatedOrder;
        if (state.orderDetails?._id === updatedOrder._id) {
          state.orderDetails = updatedOrder;
        }
      })
      .addCase(updateTrackingUrl.rejected, (state, action) => {
        state.updating = false;
        state.error    = action.payload;
      });
  },
});

export const { clearOrderError, clearOrderDetails } = orderSlice.actions;
export default orderSlice.reducer;

/* ================= SELECTORS ================= */
export const selectAllOrders    = (state) => state.orders.orders || [];
export const selectMyOrders     = (state) => state.orders.myOrders || [];
export const selectOrderDetails = (state) => state.orders.orderDetails;


export const selectPlacedOrders     = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 1);
export const selectProcessingOrders = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 2);
export const selectPrintingOrders   = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 3);
export const selectShippedOrders    = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 4);
export const selectDeliveredOrders  = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 5);
export const selectHoldOrders       = (state) => (state.orders.orders || []).filter((o) => o.orderStatus === 6);